import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { UrlBuilder } from '@venecia/builders';
import { ConfigService } from '@venecia/services';

import { SearchResponse } from '../model/search.response';
import { Id, RequestOptions } from '../model/options.request';
import { TemplateHelper } from '../helper/template.helper';
import { ApiResponse } from '../model/api.response';

export abstract class ApiTemplate<TResponse, TRequest = TResponse> {
    protected readonly http = inject(HttpClient);
    private readonly configService = inject(ConfigService);

    protected urlBuilder?: UrlBuilder;

    /**
     * @param baseUrlKey 
     * @param basePath 
     */
    protected constructor(baseUrlKey: string, basePath: string) {
        this.configService
            .select(baseUrlKey)
            .pipe(take(1))
            .subscribe((value: string) => {
                this.urlBuilder = new UrlBuilder(value, basePath);
            });
    }

    create(payload: TRequest, options?: RequestOptions): Observable<ApiResponse<TResponse>> {
        const url = this.urlBuilder!.build();
        const httpOptions = TemplateHelper.buildOptions(options);

        return this.http
            .post<ApiResponse<TResponse>>(url, payload, httpOptions)
            .pipe(
                TemplateHelper.applyRetry(options),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }


    getByPath(id: Id, options?: RequestOptions): Observable<ApiResponse<TResponse>> {
        const url = this.urlBuilder!.build(id);
        const httpOptions = TemplateHelper.buildOptions(options);

        return this.http
            .get<ApiResponse<TResponse>>(url, httpOptions)
            .pipe(
                TemplateHelper.applyRetry(options),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    search(options?: RequestOptions): Observable<ApiResponse<SearchResponse<TResponse>>> {
        const url = this.urlBuilder!.build();
        const httpOptions = TemplateHelper.buildOptions(options);

        const hasBody = options?.payload != undefined;
        const SearchResponseUrl = hasBody ? url + "/search" : url;

        const call = hasBody
            ? this.http.post<ApiResponse<SearchResponse<TResponse>>>(SearchResponseUrl, options!.payload, httpOptions)
            : this.http.get<ApiResponse<SearchResponse<TResponse>>>(SearchResponseUrl, httpOptions);

        return call
            .pipe(
                TemplateHelper.applyRetry(options),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }


    update(id: Id, payload: TRequest, options?: RequestOptions): Observable<ApiResponse<TResponse>> {
        const url = this.urlBuilder!.build(id);
        const httpOptions = TemplateHelper.buildOptions(options);

        return this.http
            .put<ApiResponse<TResponse>>(url, payload, httpOptions)
            .pipe(
                TemplateHelper.applyRetry(options),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }

    delete(id: Id, options?: RequestOptions): Observable<void> {
        const url = this.urlBuilder!.build(id);
        const httpOptions = TemplateHelper.buildOptions(options);

        return this.http
            .delete<void>(url, httpOptions)
            .pipe(
                TemplateHelper.applyRetry(options),
                catchError((err) => TemplateHelper.handleError(err))
            );
    }
}
